import React, { useCallback, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Form, Pagination } from 'react-bootstrap';
import { donationService } from '../services/api';
import './DonationReport.css';

// Shape of the grid rows as per the provided JSON
export type DonationRecord = {
  app: string;
  entity: 'RMS' | 'HYC' | string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  address: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
  purposeOfDonation: string | null;
  paymentFor: string;
  donationAmount: number;
  donationType: 'OneTime' | 'Recurring' | string;
  paymentType: 'ACH' | 'Credit Card' | string;
  // allow extra fields from API without breaking
  [k: string]: any;
};

// Pretty-print column headers from JSON keys
const toTitle = (key: string): string => {
  if (!key) return '';
  // special cases
  const specials: Record<string, string> = {
    id: 'ID',
    cvv: 'CVV',
  };
  if (specials[key]) return specials[key];
  // replace underscores with space
  let s = key.replace(/_/g, ' ');
  // add space before capitals in camelCase
  s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
  // collapse multiple spaces
  s = s.replace(/\s+/g, ' ').trim();
  // capitalize words
  s = s
    .split(' ')
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
  return s;
};

const DonationReport: React.FC = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');
  const [loginUserId, setLoginUserId] = useState<string>('');
  const [loginPassword, setLoginPassword] = useState<string>('');
  const [loginEntity, setLoginEntity] = useState<'RMS' | 'HYC'>('RMS');

  // Report state
  const [entity, setEntity] = useState<'RMS' | 'HYC'>('RMS');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [rows, setRows] = useState<DonationRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const pageSize = 20;

  // AES encryption constants
  const AES_IV = 'ao2wlsaskes5vpws';
  const AES_KEY = 'lsql93slksklasdf2psdfjqlkdf4vpws';
  const KEY_SEPARATOR = ':#:#:';

  // Convert string to Uint8Array
  const stringToUint8Array = (str: string): Uint8Array => {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  };

  // Convert Uint8Array to string
  const uint8ArrayToString = (arr: Uint8Array): string => {
    const decoder = new TextDecoder();
    return decoder.decode(arr);
  };

  // Convert Base64 string to Uint8Array
  const base64ToUint8Array = (base64: string): Uint8Array => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  // Convert Uint8Array to Unicode string (UTF-16LE)
  const uint8ArrayToUnicodeString = (arr: Uint8Array): string => {
    let result = '';
    for (let i = 0; i < arr.length; i += 2) {
      result += String.fromCharCode(arr[i] | (arr[i + 1] << 8));
    }
    return result;
  };

  // Decrypt AES encrypted data (matching C# implementation)
  const decryptPassword = async (encryptedData: string): Promise<string> => {
    try {
      const iv = stringToUint8Array(AES_IV);
      const key = stringToUint8Array(AES_KEY);

      // Import the key for AES decryption
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key as BufferSource,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );

      // Convert Base64 string to Uint8Array for decryption
      const encryptedBytes = base64ToUint8Array(encryptedData);

      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv: iv as BufferSource },
        cryptoKey,
        encryptedBytes as BufferSource
      );

      // Convert decrypted bytes to Unicode string (UTF-16LE)
      const decryptedData = uint8ArrayToUnicodeString(new Uint8Array(decryptedBuffer));
      const parts = decryptedData.split(KEY_SEPARATOR);

      if (parts.length >= 2) {
        return parts[0]; // Return the original data without GUID and timestamp
      }

      return decryptedData;
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt password');
    }
  };

  // Handle login
  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (!loginUserId.trim() || !loginPassword.trim()) {
        setAuthError('Please enter both User ID and Password');
        setAuthLoading(false);
        return;
      }

      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || '/ykrestapi';
      const endpoint = loginEntity === 'RMS'
        ? `${apiBaseUrl}/CommonChannel/ValidateUser`
        : `${apiBaseUrl}/FloweryChannel/ValidateUser`;

      // Pass only User Email to API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_Email: loginUserId,
        }),
      });

      // Handle 204 No Content - user exists but no response body
      if (response.status === 204) {
        // User exists, now validate password by comparing with entered password
        // For 204 response, we assume the user exists and password validation should proceed
        // You may need to implement additional password validation logic here
        setIsAuthenticated(true);
        setEntity(loginEntity);
        setAuthError('');
        // Clear login form
        setLoginUserId('');
        setLoginPassword('');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if user exists in API response
      if (!data || (!data.userId && !data.user_Id && !data.data?.userId && !data.data?.user_Id)) {
        setAuthError('User ID not found');
        return;
      }

      // Get the encrypted password from API response
      const encryptedPassword = data.user_Password || data.data?.user_Password || data.password || data.data?.password;
      
      if (!encryptedPassword) {
        setAuthError('Unable to validate password. Please try again.');
        return;
      }

      // Decrypt the API password using AES decryption
      const decryptedApiPassword = await decryptPassword(encryptedPassword);

      // Validate password by comparing with entered password
      if (decryptedApiPassword === loginPassword) {
        setIsAuthenticated(true);
        setEntity(loginEntity);
        setAuthError('');
        // Clear login form
        setLoginUserId('');
        setLoginPassword('');
      } else {
        setAuthError('Invalid User ID or Password');
        console.error('Password mismatch:', {
          entered: loginPassword,
          decrypted: decryptedApiPassword,
          encrypted: encryptedPassword
        });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setAuthError(`Authentication failed: ${error.message || 'Please check your credentials.'}`);
    } finally {
      setAuthLoading(false);
    }
  }, [loginUserId, loginPassword, loginEntity, decryptPassword]);

  // Build dynamic column list from current rows (union of keys)
  const columns = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) {
      Object.keys(r || {}).forEach((k) => set.add(k));
    }
    // provide stable order: some preferred keys first, then the rest sorted
    const preferred = ['app','entity','firstName','lastName','email','phoneNo','address','country','state','city','postalCode','purposeOfDonation','paymentFor','donationAmount','donationType','paymentType'];
    const rest = Array.from(set).filter((k) => !preferred.includes(k));
    rest.sort();
    const ordered = preferred.filter((k) => set.has(k)).concat(rest);
    // Hide app and entity columns per requirements
    return ordered.filter((k) => k !== 'app' && k !== 'entity');
  }, [rows]);

  // Compute a dynamic min-width per column for a slick, compact look
  const getMinWidth = useCallback((col: string): number => {
    switch (col) {
      case 'address':
        return 360;
      case 'email':
        return 220;
      case 'firstName':
      case 'lastName':
      case 'phoneNo':
      case 'country':
      case 'state':
      case 'city':
      case 'postalCode':
      case 'donationType':
      case 'paymentType':
        return 140;
      case 'purposeOfDonation':
        return 240;
      case 'paymentFor':
        return 200;
      case 'donationAmount':
        return 140;
      default:
        return 140; // sensible default for unknown keys
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await donationService.getDonations(entity, fromDate || undefined, toDate || undefined);
      setRows(data as DonationRecord[]);
      setPage(1);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('GetDonations failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entity, fromDate, toDate]);

  // Filter rows by search across all visible columns (case-insensitive)
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      Object.values(r || {}).some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  // Pagination over filtered rows
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  const exportCsv = useCallback(() => {
    const esc = (val: any) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const header = columns.map((c) => toTitle(c)).join(',');
    // export all filtered rows (not just current page)
    const lines = filteredRows.map((r) => columns.map((c) => esc(r[c])).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fname = `donations_${entity}_${fromDate || 'all'}_${toDate || 'all'}.csv`;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows, columns, entity, fromDate, toDate]);

  const totalAmount = useMemo(() => filteredRows.reduce((sum, r) => sum + (Number(r.donationAmount) || 0), 0), [filteredRows]);

  // Styles for a DataTable-like experience
  const thStyle: React.CSSProperties = {
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    background: '#f8f9fa',
    zIndex: 1,
  };
  const baseTdStyle: React.CSSProperties = {
    whiteSpace: 'nowrap',
  };
  const getThStyle = (col: string): React.CSSProperties => {
    const st: React.CSSProperties = { ...thStyle };
    st.minWidth = getMinWidth(col);
    if (col === 'donationAmount') st.textAlign = 'right';
    else st.textAlign = 'left';
    return st;
  };
  const getTdStyle = (col: string): React.CSSProperties => {
    const st: React.CSSProperties = { ...baseTdStyle };
    st.minWidth = getMinWidth(col);
    if (col === 'donationAmount') st.textAlign = 'right';
    else st.textAlign = 'left';
    return st;
  };

  // Dynamic table min-width so columns have comfortable width without cramping
  const tableMinWidth = useMemo(() => {
    return columns.reduce((sum, c) => sum + getMinWidth(c), 0) + 100;
  }, [columns, getMinWidth]);

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Container className="donation-report-container">
        <div className="login-container">
          <Card className="login-card">
            <Card.Body>
              <div className="login-header">
                <h2 className="login-title">Donation Report</h2>
                <p className="login-subtitle">Secure Access</p>
              </div>

              <Form onSubmit={handleLogin} className="login-form">
                <Form.Group className="login-form-group">
                  <Form.Label htmlFor="entitySelect">Select Organization</Form.Label>
                  <Form.Select
                    id="entitySelect"
                    value={loginEntity}
                    onChange={(e) => setLoginEntity(e.target.value as 'RMS' | 'HYC')}
                    className="login-select"
                  >
                    <option value="RMS">Radha Madhav Society (RMS)</option>
                    <option value="HYC">Harmony Youth Center (HYC)</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="login-form-group">
                  <Form.Label htmlFor="userId">User ID</Form.Label>
                  <Form.Control
                    id="userId"
                    type="text"
                    placeholder="Enter your User ID"
                    value={loginUserId}
                    onChange={(e) => setLoginUserId(e.target.value)}
                    className="login-input"
                    disabled={authLoading}
                  />
                </Form.Group>

                <Form.Group className="login-form-group">
                  <Form.Label htmlFor="password">Password</Form.Label>
                  <Form.Control
                    id="password"
                    type="password"
                    placeholder="Enter your Password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="login-input"
                    disabled={authLoading}
                  />
                </Form.Group>

                {authError && (
                  <div className="login-error">
                    <p>{authError}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="login-button"
                  disabled={authLoading}
                >
                  {authLoading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Authenticating...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container className="donation-report-container">
      {/* Brand header with styled title */}
      <div className="brand-header">
        <h1 className="brand-title">Donation Report</h1>
      </div>
      <Card className="report-filter-card mt-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col sm={12} md={3}>
              <Form.Label htmlFor="entitySelect">Select Entity</Form.Label>
              <Form.Select
                id="entitySelect"
                value={entity}
                onChange={(e) => setEntity(e.target.value as 'RMS' | 'HYC')}
              >
                <option value="RMS">RMS</option>
                <option value="HYC">HYC</option>
              </Form.Select>
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="fromDate">From</Form.Label>
              <Form.Control
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="toDate">To</Form.Label>
              <Form.Control
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="searchBox">Search</Form.Label>
              <Form.Control
                id="searchBox"
                type="text"
                placeholder="Type to filter results"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </Col>
          </Row>
          <Row className="g-2 align-items-center mt-2">
            <Col sm={12} md={6} className="d-flex" style={{ gap: 8 }}>
              <Button variant="primary" onClick={fetchData} disabled={loading} className="me-2">
                {loading ? (<><Spinner animation="border" size="sm" className="me-2" />Loading...</>) : 'Search'}
              </Button>
              <Button variant="success" onClick={exportCsv} disabled={filteredRows.length === 0}>Export to Excel</Button>
            </Col>
            <Col sm={12} md={6} className="d-flex justify-content-md-end align-items-center" style={{ gap: 12 }}>
              <div className="d-flex" style={{ gap: 6 }}>
                <Button 
                  variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'} 
                  size="sm"
                  onClick={() => { setViewMode('grid'); setPage(1); }}
                  title="Grid View"
                >
                  ⊞ Grid
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'primary' : 'outline-secondary'} 
                  size="sm"
                  onClick={() => { setViewMode('list'); setPage(1); }}
                  title="List View"
                >
                  ☰ List
                </Button>
              </div>
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                Showing {(filteredRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1)}-
                {Math.min(currentPage * pageSize, filteredRows.length)} of {filteredRows.length}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="report-data-card">
        <Card.Body>
          <div className="report-summary-stats">
            <div>Total Records: <span className="stat-value">{filteredRows.length}{rows.length !== filteredRows.length ? ` (from ${rows.length})` : ''}</span></div>
            <div>Total Donation Amount: <span className="stat-value">${totalAmount.toFixed(2)}</span></div>
          </div>
          {viewMode === 'list' ? (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table striped bordered hover size="sm" responsive className="align-middle" style={{ minWidth: tableMinWidth }}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={String(col)} style={getThStyle(String(col))}>
                        {toTitle(String(col))}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="report-empty-state">No data</td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => (
                      <tr key={idx}>
                        {columns.map((c) => (
                          <td key={String(c)} style={getTdStyle(String(c))}>
                            {String(r[c] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          ) : (
            <Row className="g-3">
              {rows.length === 0 ? (
                <Col xs={12} className="report-empty-state text-center py-5">
                  No data
                </Col>
              ) : (
                pagedRows.map((r, idx) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={idx}>
                    <Card className="donation-grid-card h-100">
                      <Card.Body>
                        <div className="grid-card-content">
                          <div className="grid-field">
                            <span className="grid-label">Name:</span>
                            <span className="grid-value">{r.firstName} {r.lastName}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Email:</span>
                            <span className="grid-value">{r.email}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Phone:</span>
                            <span className="grid-value">{r.phoneNo}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Amount:</span>
                            <span className="grid-value">${Number(r.donationAmount).toFixed(2)}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Type:</span>
                            <span className="grid-value">{r.donationType}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Payment:</span>
                            <span className="grid-value">{r.paymentType}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Address:</span>
                            <span className="grid-value grid-value-wrap">{r.address}</span>
                          </div>
                          <div className="grid-field">
                            <span className="grid-label">Location:</span>
                            <span className="grid-value">{r.city}, {r.state}</span>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              )}
            </Row>
          )}
          <div className="d-flex justify-content-center">
            <Pagination className="mb-0">
              <Pagination.First disabled={currentPage === 1} onClick={() => setPage(1)} />
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage(Math.max(1, currentPage - 1))} />
              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                // Show only first, last, current, and neighbors for large page counts
                const show = p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2;
                if (!show) {
                  // Insert ellipsis at appropriate places
                  if (p === 2 && currentPage > 4) return <Pagination.Ellipsis key="start-ell" disabled />;
                  if (p === totalPages - 1 && currentPage < totalPages - 3) return <Pagination.Ellipsis key="end-ell" disabled />;
                  return null;
                }
                return (
                  <Pagination.Item key={p} active={p === currentPage} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage(Math.min(totalPages, currentPage + 1))} />
              <Pagination.Last disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} />
            </Pagination>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DonationReport;
